import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { COMPANY, SERVICES_FULL } from "../mock";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const waLink = `https://wa.me/${COMPANY.whatsappRaw}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast({ title: "Champs requis", description: "Merci de remplir tous les champs obligatoires.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast({ title: "Demande envoyée !", description: "Merci ! Nous revenons vers vous sous 24h." });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    }, 800);
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 to-white py-16 lg:py-20">
        <div className="pointer-events-none absolute -right-16 top-0 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl animate-blob" />
        <Reveal className="relative mx-auto max-w-3xl px-5 text-center lg:px-8">
          <span className="text-sm font-semibold uppercase tracking-wider text-sky-600">Contact</span>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Souhaitez-vous recevoir un devis ?
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Remplissez le formulaire ci-dessous : nous revenons vers vous sous 24h
            avec une proposition personnalisée — sans engagement.
          </p>
        </Reveal>
      </section>

      <section className="pb-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 lg:grid-cols-5 lg:px-8">
          {/* FORM */}
          <Reveal variant="left" className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-100 bg-white p-7 shadow-md lg:p-9">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Nom complet *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Votre nom"
                    className="h-11 rounded-xl border-slate-200 focus-visible:ring-sky-500"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="vous@exemple.fr"
                    className="h-11 rounded-xl border-slate-200 focus-visible:ring-sky-500"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Téléphone *</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+33 ..."
                    className="h-11 rounded-xl border-slate-200 focus-visible:ring-sky-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Service souhaité</Label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger className="h-11 rounded-xl border-slate-200 focus:ring-sky-500">
                      <SelectValue placeholder="Choisir un service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES_FULL.map((s) => (
                        <SelectItem key={s.num} value={s.title}>{s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Votre message *</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Décrivez-nous votre besoin..."
                    rows={5}
                    className="rounded-xl border-slate-200 focus-visible:ring-sky-500"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="mt-6 h-12 w-full rounded-full bg-sky-500 text-base font-semibold text-white shadow-lg shadow-sky-500/25 transition-all hover:bg-sky-600"
              >
                {submitting ? "Envoi..." : (<><Send size={18} className="mr-2" /> Envoyer ma demande</>)}
              </Button>
              <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                <ShieldCheck size={14} className="text-sky-500" /> Vos informations restent strictement confidentielles.
              </p>
            </form>
          </Reveal>

          {/* INFO */}
          <Reveal variant="right" className="lg:col-span-2">
            <div className="rounded-3xl bg-slate-900 p-8 text-white">
              <h2 className="text-2xl font-bold">Une équipe disponible et à l'écoute.</h2>
              <div className="mt-8 space-y-5">
                <a href={`tel:${COMPANY.phoneRaw}`} className="group flex items-start gap-4 transition-colors hover:text-sky-400">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-500/20 text-sky-400 transition-transform duration-300 group-hover:scale-110"><Phone size={20} /></span>
                  <span><span className="block text-xs uppercase tracking-wider text-slate-400">Appel</span><span className="font-semibold">{COMPANY.phone}</span></span>
                </a>
                <a href={waLink} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 transition-colors hover:text-green-400">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-500/20 text-green-400 transition-transform duration-300 group-hover:scale-110"><MessageCircle size={20} /></span>
                  <span><span className="block text-xs uppercase tracking-wider text-slate-400">WhatsApp</span><span className="font-semibold">{COMPANY.whatsapp}</span></span>
                </a>
                <a href={`mailto:${COMPANY.email}`} className="group flex items-start gap-4 transition-colors hover:text-sky-400">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-500/20 text-sky-400 transition-transform duration-300 group-hover:scale-110"><Mail size={20} /></span>
                  <span><span className="block text-xs uppercase tracking-wider text-slate-400">Email</span><span className="font-semibold break-all">{COMPANY.email}</span></span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-500/20 text-sky-400"><MapPin size={20} /></span>
                  <span><span className="block text-xs uppercase tracking-wider text-slate-400">Adresse</span><span className="font-semibold">{COMPANY.address}</span></span>
                </div>
              </div>
              <p className="mt-8 border-t border-slate-800 pt-6 text-sm leading-relaxed text-slate-400">
                30 ans à perfectionner notre métier — pour vous offrir un nettoyage
                à la hauteur de vos attentes.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
